/**
 * Surface Camera Orientation Tool
 *
 * Aligns the orthographic camera roll in a surface's 2D view by drawing a line
 * (drag, or click-click) that should become horizontal. A floor-free counterpart
 * of the floorplan persona's CameraOrientationTool — no FloorplanToolBase, no floor
 * isolation, and no wall auto-detection. Only the manual two-point alignment, which
 * is what the surface workflow needs.
 *
 * Icon: undo-dot.svg
 */

import { BaseTool } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraMode } from '../../../defines';
import { CameraAlignmentTool } from '../../../utils/CameraAlignmentTool';
import * as THREE from 'three';

export class SurfaceCameraOrientationTool extends BaseTool {
  readonly name = 'surface_camera_orientation';

  /** Reusable two-point camera roll alignment (visuals + roll math). */
  private cameraAlignmentTool: CameraAlignmentTool;

  /** Mouse-down position, to distinguish a drag from a click. */
  private mouseDownPos: THREE.Vector2 | null = null;
  private readonly DRAG_THRESHOLD = 5;

  /** Click-click mode: first point placed, waiting for the second click. */
  private waitingForSecondClick = false;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.cameraAlignmentTool = new CameraAlignmentTool(viewer);
  }

  protected onEnable(): void {
    this.viewer.renderArea.style.cursor = 'crosshair';
  }

  protected onDisable(): void {
    this.viewer.renderArea.style.cursor = '';
    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    }
    this.mouseDownPos = null;
    this.waitingForSecondClick = false;
  }

  /** Surface orientation only makes sense in 2D ortho views. */
  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.ORTHOGRAPHIC_2D_TOP, ViewModes.XRAY_2D],
      defaultViewId: 'orthographic_2d_top',
    };
  }

  private isIn2D(): boolean {
    return this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
  }

  public onMouseDown(event: MouseEvent): void {
    if (!this.isIn2D()) return;
    // Shift/ctrl are reserved for camera panning.
    if (event.shiftKey || event.ctrlKey) return;
    if (event.button !== 0) return;

    this.mouseDownPos = this.getMousePosition(event);

    // While waiting for the second click, don't restart alignment on mouse down.
    if (this.waitingForSecondClick) return;

    if (!this.cameraAlignmentTool.isAlignmentActive()) {
      const worldPos = this.mouseDownPos ? this.screenToWorld(this.mouseDownPos) : null;
      if (worldPos) this.cameraAlignmentTool.start(worldPos);
    }
  }

  public onMouseMove(event: MouseEvent): void {
    if (!this.isIn2D()) return;
    if (!this.cameraAlignmentTool.isAlignmentActive()) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;
    const worldPos = this.screenToWorld(mouse);
    if (worldPos) this.cameraAlignmentTool.updateEndPoint(worldPos);
  }

  public onMouseUp(event: MouseEvent): void {
    if (!this.isIn2D()) return;
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    const isDrag = this.mouseDownPos && this.isSignificantMove(this.mouseDownPos, mouse);

    if (this.waitingForSecondClick) {
      // Second click completes the alignment.
      const worldPos = this.screenToWorld(mouse);
      if (worldPos && this.cameraAlignmentTool.isAlignmentActive()) {
        this.cameraAlignmentTool.complete(worldPos);
      }
      this.waitingForSecondClick = false;
    } else if (isDrag) {
      // Drag completes the alignment at the release point.
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        const worldPos = this.screenToWorld(mouse);
        if (worldPos) this.cameraAlignmentTool.complete(worldPos);
      }
    } else {
      // First click placed the start point (in onMouseDown) — wait for the second.
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        this.waitingForSecondClick = true;
      }
    }

    this.mouseDownPos = null;
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.cameraAlignmentTool.isAlignmentActive() || this.waitingForSecondClick) {
        this.cameraAlignmentTool.cancel();
        this.waitingForSecondClick = false;
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  private isSignificantMove(start: THREE.Vector2, end: THREE.Vector2): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return Math.sqrt(dx * dx + dy * dy) > this.DRAG_THRESHOLD;
  }

  private getMousePosition(event: MouseEvent): THREE.Vector2 | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Unproject a screen pixel onto the active 2D plane (the surface's associated
   * plane when present, else the z=0 ground plane for top-down).
   */
  private screenToWorld(screen: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const ndcX = (screen.x / viewportWidth) * 2 - 1;
    const ndcY = -(screen.y / viewportHeight) * 2 + 1;

    const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
    const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);
    const rayDirection = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();
    const ray = new THREE.Ray(nearPoint, rayDirection);

    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    if (associatedPlane) {
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane.getNormal(), associatedPlane.position.clone());
      const intersection = ray.intersectPlane(plane, new THREE.Vector3());
      if (intersection) return intersection;
    }

    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    return ray.intersectPlane(groundPlane, new THREE.Vector3());
  }

  dispose(): void {
    this.cameraAlignmentTool.dispose();
    super.dispose();
  }
}
